import { create } from 'zustand';
import { useZoneStore } from './zoneStore';
import { couplingService } from '../services/couplingService';
import type { CouplingFlow, ZoneCoupling, ZoneVerbund } from '../types/coupling';

/**
 * Trennstellen of the active zone (doc/Architektur/NETZVERBUND.md 6). Zone-scoped: cleared and
 * reloaded on every zone switch (subscription below), never mixed into networkElementsStore —
 * coupling geometry must not influence map bounds, hit tests or element statistics.
 */
interface CouplingState {
  zoneId: string | null;
  couplings: ZoneCoupling[];
  verbund: ZoneVerbund | null;
  isLoading: boolean;
  error: string | null;
  /** Map layer visibility (UI preference, not persisted per zone). */
  showTrennstellen: boolean;
  selectedCouplingId: string | null;
  /** Latest load-flow figures per coupling id (`couplingFlows` of /powercalculation; empty in archive playback). */
  flows: Record<string, CouplingFlow>;
}

interface CouplingActions {
  load: (zoneId: string) => Promise<void>;
  clear: () => void;
  setShowTrennstellen: (show: boolean) => void;
  selectCoupling: (id: string | null) => void;
  /** Replace the figures with those of the latest power calculation response. */
  setFlows: (flows: CouplingFlow[]) => void;
  /** Switch a coupling; the backend journals the change and the pool picks up the new units. */
  setState: (couplingId: string, isClosed: boolean, note?: string) => Promise<void>;
  /** Assign the coupling's switch to a busbar of its KVS (drag & drop in the busbar view). */
  setBusbar: (couplingId: string, busbarId: string | null) => Promise<void>;
}

let loadSeq = 0;

export const useCouplingStore = create<CouplingState & CouplingActions>()((set, get) => ({
  zoneId: null,
  couplings: [],
  verbund: null,
  isLoading: false,
  error: null,
  showTrennstellen: true,
  selectedCouplingId: null,
  flows: {},

  load: async (zoneId: string) => {
    const seq = ++loadSeq;
    set({ zoneId, isLoading: true, error: null });
    try {
      const [couplings, verbund] = await Promise.all([
        couplingService.getCouplings(zoneId),
        couplingService.getVerbund(zoneId).catch(() => null),
      ]);
      if (seq !== loadSeq || useZoneStore.getState().activeZoneId !== zoneId) return; // stale
      set({ couplings, verbund, isLoading: false });
    } catch (error) {
      if (seq !== loadSeq) return;
      set({ couplings: [], verbund: null, isLoading: false, error: error instanceof Error ? error.message : 'Trennstellen konnten nicht geladen werden' });
    }
  },

  clear: () => {
    loadSeq++;
    set({ zoneId: null, couplings: [], verbund: null, isLoading: false, error: null, selectedCouplingId: null, flows: {} });
  },

  setShowTrennstellen: (show: boolean) => set({ showTrennstellen: show }),

  selectCoupling: (id: string | null) => set({ selectedCouplingId: id }),

  setFlows: (flows: CouplingFlow[]) => {
    const next: Record<string, CouplingFlow> = {};
    for (const flow of flows) next[flow.couplingId] = flow;
    set({ flows: next });
  },

  setBusbar: async (couplingId: string, busbarId: string | null) => {
    const zoneId = get().zoneId;
    if (!zoneId) return;
    const updated = await couplingService.setBusbar(zoneId, couplingId, busbarId);
    set(state => ({ couplings: state.couplings.map(c => (c.id === couplingId ? updated : c)) }));
  },

  setState: async (couplingId: string, isClosed: boolean, note?: string) => {
    const zoneId = get().zoneId;
    if (!zoneId) return;
    const updated = await couplingService.setState(zoneId, couplingId, isClosed, note);
    set(state => ({ couplings: state.couplings.map(c => (c.id === couplingId ? updated : c)) }));
    // membership of the calculation unit may have changed
    try {
      const verbund = await couplingService.getVerbund(zoneId);
      if (get().zoneId === zoneId) set({ verbund });
    } catch {
      /* badge simply keeps the previous unit */
    }
  },
}));

// Zone switch: drop the old zone's Trennstellen and load the new ones (same pattern as
// historicalPlaybackStore — every switch path is covered, not only ControlSidebar).
let lastZoneId: string | null = useZoneStore.getState().activeZoneId;
useZoneStore.subscribe(state => {
  if (state.activeZoneId !== lastZoneId) {
    lastZoneId = state.activeZoneId;
    useCouplingStore.getState().clear();
    if (state.activeZoneId) {
      void useCouplingStore.getState().load(state.activeZoneId);
    }
  }
});
if (lastZoneId) {
  void useCouplingStore.getState().load(lastZoneId);
}
